// src/pages/pet/PetManagement.js
// Página para gestionar mascotas con listado, paginación, búsqueda y modal de creación

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Modal, InputGroup, Alert } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getPets, createPet, updatePet, getPetsByClientId } from '../../services/petApi';
import { getCustomers } from '../../services/customerApi';
import PetForm from './PetForm';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const PetManagement = () => {
  const { user } = useAuth();
  
  // Estados para el listado
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  
  // Estados para paginación y búsqueda
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientIdSearch, setClientIdSearch] = useState('');
  const [searchByClientId, setSearchByClientId] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPets, setTotalPets] = useState(0);
  
  // Estados para el modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedPetNotes, setSelectedPetNotes] = useState('');
  
  // Cargar mascotas
  const loadPets = useCallback(async () => {
    try {
      setLoading(true);
      
      // Si estamos buscando por identificación de cliente, usar esa búsqueda
      if (searchByClientId && clientIdSearch.trim()) {
        try {
          // Primero buscar el cliente por identificación (documento)
          const { ok: customersOk, data: customersData } = await getCustomers({ 
            page: 1, 
            per_page: 10, 
            q: clientIdSearch.trim() 
          });

          if (customersOk && customersData?.code === 200) {
            const responseData = customersData.data || customersData.message || [];
            const clients = responseData.customers || responseData;
            
            if (clients.length === 0) {
              setPets([]);
              setTotalPets(0);
              setTotalPages(1);
              showAlert('No se encontró ningún cliente con esa identificación', 'error');
              return;
            }

            // Buscar el cliente que coincida exactamente con el documento ingresado
            let client = null;
            for (const c of clients) {
              // Verificar si el documento del cliente coincide con la búsqueda
              const clientDocument = c.person?.document || c.document || '';
              if (clientDocument.toString().trim() === clientIdSearch.trim()) {
                client = c;
                break;
              }
            }
            
            // Si no encontramos coincidencia exacta, usar el primero
            if (!client) {
              client = clients[0];
            }
            
            // Verificar que el cliente tenga id_client
            if (!client.id_client) {
              console.error('Cliente encontrado pero sin id_client:', client);
              setPets([]);
              setTotalPets(0);
              setTotalPages(1);
              showAlert('Error: El cliente encontrado no tiene un ID válido', 'error');
              return;
            }
            
            console.log('Cliente encontrado:', client);
            console.log('Buscando mascotas para cliente ID:', client.id_client);
            
            // Obtener las mascotas del cliente usando su ID
            const { ok: petsOk, status: petsStatus, data: petsData } = await getPetsByClientId(client.id_client);
            
            if (petsOk && (petsStatus === 200 || petsData?.code === 200)) {
              const petsResponseData = petsData.data || petsData.message || [];
              const petsList = Array.isArray(petsResponseData) ? petsResponseData : [];
              setPets(petsList);
              setTotalPets(petsList.length);
              setTotalPages(1);
            } else {
              setPets([]);
              setTotalPets(0);
              setTotalPages(1);
              showAlert('No se encontraron mascotas para este cliente', 'info');
            }
          } else {
            setPets([]);
            setTotalPets(0);
            setTotalPages(1);
            showAlert('Error al buscar el cliente', 'error');
          }
        } catch (error) {
          console.error('Error buscando mascotas por cliente:', error);
          showAlert('Error al buscar mascotas por identificación de cliente', 'error');
        } finally {
          setLoading(false);
        }
        return;
      }
      
      // Búsqueda normal (solo si no estamos buscando por cliente)
      if (searchByClientId) {
        // Si estamos buscando por cliente pero llegamos aquí, algo salió mal
        console.warn('Búsqueda normal ejecutada mientras searchByClientId es true');
        return;
      }
      
      const params = {
        page: currentPage,
        per_page: perPage,
        q: searchQuery.trim()
      };
      
      const { ok, status, data } = await getPets(params);
      
      if (ok && (status === 200 || data?.code === 200)) {
        setTotalPets(data?.total);
        const responseData = data.data || data.message || [];
        setPets(responseData.pets || responseData);
        
        // Manejar metadatos de paginación
        if (data?.per_page) {
          setTotalPages(data?.total_pages || 1);
          setTotalPets(data?.total);
        } else if (responseData.total) {
          setTotalPages(Math.ceil(responseData.total / perPage));
          setTotalPets(1);
        }
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error cargando mascotas';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error cargando mascotas:', error);
      showAlert('Error inesperado al cargar mascotas', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, searchQuery, clientIdSearch, searchByClientId]);

  // Cargar mascotas al montar el componente (solo carga inicial)
  useEffect(() => {
    // Solo cargar en la carga inicial si no estamos buscando por cliente
    if (!searchByClientId && !searchQuery && !clientIdSearch) {
      loadPets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo en el montaje inicial

  // Manejar búsqueda con debounce (solo si no estamos buscando por cliente)
  useEffect(() => {
    // No ejecutar si estamos buscando por identificación de cliente
    if (searchByClientId) {
      return;
    }
    
    // No ejecutar si searchQuery está vacío (carga inicial se maneja en otro useEffect)
    if (!searchQuery.trim()) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadPets();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchByClientId, loadPets, currentPage]);

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Manejar cambio de elementos por página
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Manejar búsqueda
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setClientIdSearch('');
    setSearchByClientId(false);
  };

  // Manejar búsqueda por identificación de cliente
  const handleClientIdSearchChange = (e) => {
    setClientIdSearch(e.target.value);
  };

  // Ejecutar búsqueda por identificación de cliente
  const handleSearchByClientId = async () => {
    if (!clientIdSearch.trim()) {
      return;
    }
    
    // Establecer estados primero
    setSearchByClientId(true);
    setSearchQuery('');
    setCurrentPage(1);
    
    // Ejecutar la búsqueda directamente sin depender de useEffect
    try {
      setLoading(true);
      
      // Primero buscar el cliente por identificación (documento)
      const { ok: customersOk, data: customersData } = await getCustomers({ 
        page: 1, 
        per_page: 10, 
        q: clientIdSearch.trim() 
      });

      if (customersOk && customersData?.code === 200) {
        const responseData = customersData.data || customersData.message || [];
        const clients = responseData.customers || responseData;
        
        if (clients.length === 0) {
          setPets([]);
          setTotalPets(0);
          setTotalPages(1);
          showAlert('No se encontró ningún cliente con esa identificación', 'error');
          return;
        }

        // Buscar el cliente que coincida exactamente con el documento ingresado
        let client = null;
        for (const c of clients) {
          // Verificar si el documento del cliente coincide con la búsqueda
          const clientDocument = c.person?.document || c.document || '';
          if (clientDocument.toString().trim() === clientIdSearch.trim()) {
            client = c;
            break;
          }
        }
        
        // Si no encontramos coincidencia exacta, usar el primero
        if (!client) {
          client = clients[0];
        }
        
        // Verificar que el cliente tenga id_client
        if (!client.id_client) {
          console.error('Cliente encontrado pero sin id_client:', client);
          setPets([]);
          setTotalPets(0);
          setTotalPages(1);
          showAlert('Error: El cliente encontrado no tiene un ID válido', 'error');
          return;
        }
        
        console.log('Cliente encontrado:', client);
        console.log('Buscando mascotas para cliente ID:', client.id_client);
        
        // Obtener las mascotas del cliente usando su ID
        const { ok: petsOk, status: petsStatus, data: petsData } = await getPetsByClientId(client.id_client);
        
        if (petsOk && (petsStatus === 200 || petsData?.code === 200)) {
          const petsResponseData = petsData.data || petsData.message || [];
          const petsList = Array.isArray(petsResponseData) ? petsResponseData : [];
          console.log('Mascotas encontradas:', petsList);
          setPets(petsList);
          setTotalPets(petsList.length);
          setTotalPages(1);
        } else {
          setPets([]);
          setTotalPets(0);
          setTotalPages(1);
          showAlert('No se encontraron mascotas para este cliente', 'info');
        }
      } else {
        setPets([]);
        setTotalPets(0);
        setTotalPages(1);
        showAlert('Error al buscar el cliente', 'error');
      }
    } catch (error) {
      console.error('Error buscando mascotas por cliente:', error);
      showAlert('Error al buscar mascotas por identificación de cliente', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Limpiar búsqueda por cliente
  const handleClearClientIdSearch = () => {
    setClientIdSearch('');
    setSearchByClientId(false);
    setCurrentPage(1);
  };

  // Abrir modal de creación
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  // Cerrar modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPet(null);
  };

  // Abrir modal de edición
  const handleOpenEditModal = (pet) => {
    console.log(pet);
    setSelectedPet(pet);
    setShowEditModal(true);
  };

  // Abrir modal de notas
  const handleOpenNotesModal = (pet) => {
    setSelectedPet(pet);
    setSelectedPetNotes(pet.notes || '');
    setShowNotesModal(true);
  };

  // Cerrar modal de notas
  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedPet(null);
    setSelectedPetNotes('');
  };

  // Manejar creación de mascota
  const handleCreatePet = async (petData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await createPet(petData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Mascota creada exitosamente', 'success');
        setShowCreateModal(false);
        // Recargar la lista
        loadPets();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error creando mascota';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error creando mascota:', error);
      showAlert('Error inesperado al crear mascota', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Manejar actualización de mascota
  const handleUpdatePet = async (petData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await updatePet(selectedPet.id_pet, petData);
      
      if (ok && (status === 200 || data?.code === 200)) {
        showAlert(data?.message || 'Mascota actualizada exitosamente', 'success');
        setShowEditModal(false);
        setSelectedPet(null);
        // Recargar la lista
        loadPets();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error actualizando mascota';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error actualizando mascota:', error);
      showAlert('Error inesperado al actualizar mascota', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Generar array de páginas para la paginación
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Calcular edad de la mascota
  const calculateAge = (birthdate) => {
    if (!birthdate) return 'N/A';
    const birth = new Date(birthdate);
    const today = new Date();
    const ageInMonths = Math.floor((today - birth) / (1000 * 60 * 60 * 24 * 30.44));
    
    if (ageInMonths < 12) {
      return `${ageInMonths} meses`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years} años ${months} meses` : `${years} años`;
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Gestión de Mascotas</h4>
                <Button variant="primary" onClick={handleOpenCreateModal}>
                  <i className="fas fa-plus me-2"></i>
                  Nueva Mascota
                </Button>
              </Card.Header>
              <Card.Body>

                {/* Controles de búsqueda y filtros */}
                <Row className="mb-3">
                  <Col md={5}>
                    <Form.Label>Buscar por nombre, raza, cliente</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, raza, cliente..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        disabled={searchByClientId}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Buscar por identificación de cliente</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-id-card"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Ingrese identificación del cliente..."
                        value={clientIdSearch}
                        onChange={handleClientIdSearchChange}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSearchByClientId();
                          }
                        }}
                        disabled={!!searchQuery}
                      />
                      <Button
                        variant="primary"
                        onClick={handleSearchByClientId}
                        disabled={!clientIdSearch.trim() || !!searchQuery || loading}
                      >
                        <i className="fas fa-search"></i>
                      </Button>
                      {searchByClientId && (
                        <Button
                          variant="outline-secondary"
                          onClick={handleClearClientIdSearch}
                          disabled={loading}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Elementos por página</Form.Label>
                    <Form.Select value={perPage} onChange={handlePerPageChange} disabled={searchByClientId}>
                      <option value={10}>10 por página</option>
                      <option value={20}>20 por página</option>
                      <option value={50}>50 por página</option>
                      <option value={100}>100 por página</option>
                    </Form.Select>
                  </Col>
                </Row>
                {searchByClientId && (
                  <Alert variant="info" className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    Mostrando mascotas del cliente con identificación: <strong>{clientIdSearch}</strong>
                    <Button
                      variant="link"
                      size="sm"
                      className="ms-2 p-0"
                      onClick={handleClearClientIdSearch}
                    >
                      Limpiar búsqueda
                    </Button>
                  </Alert>
                )}
                <Row className="mb-3">
                  <Col>
                    <small className="text-muted">
                      Total: {totalPets} mascotas
                    </small>
                  </Col>
                </Row>

                {/* Tabla de mascotas */}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Imagen</th>
                          <th>Nombre</th>
                          <th>Cliente</th>
                          <th>Especie</th>
                          <th>Raza</th>
                          <th>Peso (kg)</th>
                          <th>Edad</th>
                          <th>Notas</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pets.length === 0 ? (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              {searchQuery ? 'No se encontraron mascotas con ese criterio de búsqueda' : 'No hay mascotas registradas'}
                            </td>
                          </tr>
                        ) : (
                          pets.map((pet) => (
                            <tr key={pet.id_pet}>
                              <td>{pet.id_pet}</td>
                              <td>
                                {pet.profile_image_url ? (
                                  <img
                                    src={pet.profile_image_url}
                                    alt={pet.name || 'Mascota'}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '50%',
                                      border: '2px solid #dee2e6'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    display: pet.profile_image_url ? 'none' : 'flex',
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f0f0f0',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #dee2e6'
                                  }}
                                >
                                  <i className="fas fa-paw text-muted"></i>
                                </div>
                              </td>
                              <td>{pet.name || 'N/A'}</td>
                              <td>
                                {pet.name_client
                                }
                              </td>
                              <td>
                                {pet.id_specie === 1 ? 'Perro' :
                                 pet.id_specie === 2 ? 'Gato' :
                                 pet.id_specie === 3 ? 'Ave' :
                                 pet.id_specie === 4 ? 'Conejo' :
                                 pet.id_specie === 5 ? 'Hamster' : 'Otro'}
                              </td>
                              <td>{pet.breed || 'N/A'}</td>
                              <td>{pet.weight_kg || 'N/A'}</td>
                              <td>{calculateAge(pet.birthdate)}</td>
                              <td>
                                {pet.notes ? (
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => handleOpenNotesModal(pet)}
                                    title="Ver notas completas"
                                  >
                                    <i className="fas fa-sticky-note me-1"></i>
                                    Ver Notas
                                  </Button>
                                ) : (
                                  <span className="text-muted">Sin notas</span>
                                )}
                              </td>
                              <td>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleOpenEditModal(pet)}
                                >
                                  <i className="fas fa-edit">Editar</i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>

                    {/* Paginación */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <small className="text-muted">
                            Página {currentPage} de {totalPages}
                          </small>
                        </div>
                        <div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="me-2"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </Button>
                          
                          {generatePageNumbers().map(page => (
                            <Button
                              key={page}
                              variant={page === currentPage ? "primary" : "outline-secondary"}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="me-2"
                            >
                              {page}
                            </Button>
                          ))}
                          
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de creación de mascota */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Crear Nueva Mascota</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PetForm
              onSubmit={handleCreatePet}
              loading={modalLoading}
            />
          </Modal.Body>
        </Modal>

        {/* Modal de edición de mascota */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Editar Mascota</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPet && (
              <PetForm
                onSubmit={handleUpdatePet}
                loading={modalLoading}
                initialData={selectedPet}
                submitText="Actualizar Mascota"
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de notas de mascota */}
        <Modal show={showNotesModal} onHide={handleCloseNotesModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Notas de {selectedPet ? selectedPet.name : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <h6>Información de la Mascota:</h6>
              <p className="text-muted mb-0">
                <strong>Nombre:</strong> {selectedPet?.name || 'N/A'}<br/>
                <strong>Cliente:</strong> {selectedPet?.name_client || 'N/A'}<br/>
                <strong>Especie:</strong> {
                  selectedPet?.id_specie === 1 ? 'Perro' :
                  selectedPet?.id_specie === 2 ? 'Gato' :
                  selectedPet?.id_specie === 3 ? 'Ave' :
                  selectedPet?.id_specie === 4 ? 'Conejo' :
                  selectedPet?.id_specie === 5 ? 'Hamster' : 'Otro'
                }<br/>
                <strong>Raza:</strong> {selectedPet?.breed || 'N/A'}
              </p>
            </div>
            
            <div>
              <h6>Notas:</h6>
              {selectedPetNotes ? (
                <div 
                  className="border rounded p-3 bg-light"
                  style={{ 
                    minHeight: '200px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                >
                  {selectedPetNotes}
                </div>
              ) : (
                <div className="border rounded p-3 bg-light text-muted text-center" style={{ minHeight: '200px' }}>
                  <i className="fas fa-sticky-note fa-2x mb-2"></i>
                  <p className="mb-0">No hay notas registradas para esta mascota</p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNotesModal}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default PetManagement;
